import { CSRF_HEADER, CSRF_HEADER_VALUE, SCOPE_HEADER } from '@platform/contracts';

/** Error de la API con código estable (`error`) y mensaje en español para mostrar. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export interface ApiOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Alcance (all | client:<slug> | tenant:<slug>) → header X-Scope. */
  scope?: string;
  signal?: AbortSignal;
}

const MUTATIONS = new Set(['POST', 'PATCH', 'PUT', 'DELETE']);

function isErrorBody(v: unknown): v is { error: string; message: string; details?: unknown } {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as { error?: unknown }).error === 'string' &&
    typeof (v as { message?: unknown }).message === 'string'
  );
}

/**
 * fetch mismo origen con cookie de sesión. Toda mutación manda el header anti-CSRF que la API exige.
 * La SPA nunca ve tokens: la sesión es una cookie httpOnly.
 */
export async function api<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = { accept: 'application/json' };
  if (MUTATIONS.has(method)) headers[CSRF_HEADER] = CSRF_HEADER_VALUE;
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  if (opts.scope) headers[SCOPE_HEADER] = opts.scope;

  const res = await fetch(path, {
    method,
    headers,
    credentials: 'same-origin',
    ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
    ...(opts.signal ? { signal: opts.signal } : {}),
  });

  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }
  if (!res.ok) {
    if (isErrorBody(data)) throw new ApiError(res.status, data.error, data.message, data.details);
    throw new ApiError(res.status, 'http_error', `Error ${String(res.status)}`);
  }
  return data as T;
}

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Algo salió mal';
}
